import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  DsAlert,
  DsButton,
  DsInput,
  DsModal,
  DsSegmented,
  DsTextarea,
} from '@/modules/internal-v2/ds';
import {
  useCreateAnnouncement,
  useUpdateAnnouncement,
} from '@/api/announcements/announcements.hooks';
import {
  Announcement,
  AnnouncementAudience,
  AnnouncementDisplayMode,
  AnnouncementPlacement,
  AnnouncementVariant,
} from '@/api/announcements/announcements.types';

interface Props {
  announcement: Announcement | null;
  onClose: () => void;
}

const VARIANTS: { value: AnnouncementVariant; label: string }[] = [
  { value: 'INFO', label: 'Info' },
  { value: 'SUCCESS', label: 'Erfolg' },
  { value: 'WARNING', label: 'Warnung' },
  { value: 'ERROR', label: 'Fehler' },
];

const DISPLAY_MODES: { value: AnnouncementDisplayMode; label: string }[] = [
  { value: 'BANNER', label: 'Banner' },
  { value: 'POPUP', label: 'Popup' },
  { value: 'BOTH', label: 'Beides' },
];

const AUDIENCES: { value: AnnouncementAudience; label: string }[] = [
  { value: 'ALL', label: 'Alle' },
  { value: 'EXTERNAL', label: 'Externe' },
  { value: 'INTERNAL', label: 'Interne' },
];

const PLACEMENTS: { value: AnnouncementPlacement; label: string }[] = [
  { value: 'GLOBAL', label: 'Überall' },
  { value: 'EXTERNAL_PORTAL', label: 'Kundenportal' },
  { value: 'INTERNAL_ADMIN', label: 'Admin-Bereich' },
  { value: 'LOGIN', label: 'Login-Seite' },
];

const ALERT_SEVERITY: Record<
  AnnouncementVariant,
  'info' | 'success' | 'warn' | 'error'
> = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warn',
  ERROR: 'error',
};

function errorMessage(error: AxiosError): string {
  return (
    (error.response?.data as { message?: string })?.message ??
    'Speichern fehlgeschlagen.'
  );
}

/** ISO string -> value for <input type="datetime-local"> (local time). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}

function localInputToIso(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

export function AnnouncementEditor({ announcement, onClose }: Props) {
  const createAnnouncement = useCreateAnnouncement();
  const updateAnnouncement = useUpdateAnnouncement();

  const [title, setTitle] = useState(announcement?.title ?? '');
  const [body, setBody] = useState(announcement?.body ?? '');
  const [variant, setVariant] = useState<AnnouncementVariant>(
    announcement?.variant ?? 'INFO',
  );
  const [displayMode, setDisplayMode] = useState<AnnouncementDisplayMode>(
    announcement?.displayMode ?? 'BANNER',
  );
  const [placement, setPlacement] = useState<Set<AnnouncementPlacement>>(
    new Set(announcement?.placement ?? ['GLOBAL']),
  );
  const [audience, setAudience] = useState<AnnouncementAudience>(
    announcement?.audience ?? 'ALL',
  );
  const [dismissible, setDismissible] = useState(
    announcement?.dismissible ?? true,
  );
  const [ctaLabel, setCtaLabel] = useState(announcement?.ctaLabel ?? '');
  const [ctaUrl, setCtaUrl] = useState(announcement?.ctaUrl ?? '');
  const [startsAt, setStartsAt] = useState(
    isoToLocalInput(announcement?.startsAt ?? null),
  );
  const [endsAt, setEndsAt] = useState(
    isoToLocalInput(announcement?.endsAt ?? null),
  );
  const [isActive, setIsActive] = useState(announcement?.isActive ?? false);
  const [priority, setPriority] = useState(
    String(announcement?.priority ?? 0),
  );
  const [error, setError] = useState<string | null>(null);

  const isSaving = createAnnouncement.isPending || updateAnnouncement.isPending;

  const togglePlacement = (value: AnnouncementPlacement) => {
    setPlacement((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSave = () => {
    setError(null);
    if (!title.trim()) {
      setError('Titel ist erforderlich.');
      return;
    }
    if (placement.size === 0) {
      setError('Mindestens eine Platzierung ist erforderlich.');
      return;
    }
    const body_ = {
      title: title.trim(),
      body: body.trim() || null,
      variant,
      displayMode,
      placement: [...placement],
      audience,
      dismissible,
      ctaLabel: ctaLabel.trim() || null,
      ctaUrl: ctaUrl.trim() || null,
      startsAt: localInputToIso(startsAt),
      endsAt: localInputToIso(endsAt),
      isActive,
      priority: Number.parseInt(priority, 10) || 0,
    };
    const opts = {
      onSuccess: onClose,
      onError: (e: AxiosError) => setError(errorMessage(e)),
    };
    if (announcement) {
      updateAnnouncement.mutate({ id: announcement.id, body: body_ }, opts);
    } else {
      createAnnouncement.mutate(body_, opts);
    }
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title={announcement ? 'Ankündigung bearbeiten' : 'Neue Ankündigung'}
      footer={
        <>
          <DsButton onClick={onClose}>Abbrechen</DsButton>
          <DsButton variant="primary" disabled={isSaving} onClick={handleSave}>
            {isSaving ? 'Speichert…' : 'Speichern'}
          </DsButton>
        </>
      }
    >
      <div className="ann-form">
        {error && <div className="ds-alert error">{error}</div>}

        <label className="ann-field">
          <span className="ann-field-label">Titel</span>
          <DsInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <label className="ann-field">
          <span className="ann-field-label">Text</span>
          <DsTextarea
            value={body}
            rows={3}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>

        <div className="ann-row-2">
          <div className="ann-field">
            <span className="ann-field-label">Stil</span>
            <DsSegmented
              ariaLabel="Stil"
              options={VARIANTS}
              value={variant}
              onChange={setVariant}
            />
          </div>
          <div className="ann-field">
            <span className="ann-field-label">Darstellung</span>
            <DsSegmented
              ariaLabel="Darstellung"
              options={DISPLAY_MODES}
              value={displayMode}
              onChange={setDisplayMode}
            />
          </div>
        </div>

        <div className="ann-field">
          <span className="ann-field-label">Zielgruppe</span>
          <DsSegmented
            ariaLabel="Zielgruppe"
            options={AUDIENCES}
            value={audience}
            onChange={setAudience}
          />
        </div>

        <div className="ann-field">
          <span className="ann-field-label">Platzierung</span>
          <div className="ann-checks">
            {PLACEMENTS.map((p) => (
              <label key={p.value} className="ann-check">
                <input
                  type="checkbox"
                  checked={placement.has(p.value)}
                  onChange={() => togglePlacement(p.value)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </div>

        <div className="ann-row-2">
          <label className="ann-field">
            <span className="ann-field-label">CTA-Beschriftung</span>
            <DsInput
              value={ctaLabel}
              placeholder="optional"
              onChange={(e) => setCtaLabel(e.target.value)}
            />
          </label>
          <label className="ann-field">
            <span className="ann-field-label">CTA-Link</span>
            <DsInput
              value={ctaUrl}
              placeholder="optional"
              onChange={(e) => setCtaUrl(e.target.value)}
            />
          </label>
        </div>

        <div className="ann-row-2">
          <label className="ann-field">
            <span className="ann-field-label">Sichtbar ab</span>
            <input
              type="datetime-local"
              className="ann-datetime"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </label>
          <label className="ann-field">
            <span className="ann-field-label">Sichtbar bis</span>
            <input
              type="datetime-local"
              className="ann-datetime"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </label>
        </div>

        <div className="ann-inline">
          <label className="ann-check">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Aktiv
          </label>
          <label className="ann-check">
            <input
              type="checkbox"
              checked={dismissible}
              onChange={(e) => setDismissible(e.target.checked)}
            />
            Schließbar
          </label>
          <label className="ann-check">
            Priorität
            <DsInput
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{ width: 80 }}
            />
          </label>
        </div>

        <div className="ann-field">
          <span className="ann-field-label">Vorschau</span>
          <DsAlert severity={ALERT_SEVERITY[variant]}>
            <strong>{title || 'Titel'}</strong>
            {body && <div style={{ marginTop: 4 }}>{body}</div>}
          </DsAlert>
        </div>
      </div>
    </DsModal>
  );
}
